<template>
    <div
        class="vdp-image-swiper tw-flex tw-flex-col tw-gap-3 md:tw-gap-6 tw-pt-8 md:tw-pt-16 tw-pb-3 md:tw-pb-6 tw-bg-secondary-light"
    >
        <swiper
            ref="swiper"
            :options="swiperOptions"
            class="vdp-image-swiper__container"
            @tap="onTap"
            @ready="onReady"
            @resize="onResize"
        >
            <!-- @slot default slot for DkSlider component -->
            <slot></slot>
        </swiper>

        <!-- Optional controls -->
        <div class="vdp-image-swiper__controls tw-flex tw-justify-center tw-gap-4">
            <slot name="button-prev" v-bind="{ _uid }">
                <div :class="`vdp-image-swiper__button-prev prev-${_uid}`">
                    <button
                        class="tw-inline-flex tw-justify-center tw-items-center tw-w-10 tw-h-10 tw-outline-none tw-cursor-pointer hover:md:tw-text-slate-500 md:tw-ease-out md:tw-duration-200 active:md:-tw-translate-x-1"
                    >
                        <dk-icon name="ds-chevron-left" size="s" />
                    </button>
                </div>
            </slot>
            <slot name="button-next" v-bind="{ _uid }">
                <div :class="`vdp-image-swiper__button-next next-${_uid}`">
                    <button
                        class="tw-inline-flex tw-justify-center tw-items-center tw-w-10 tw-h-10 tw-outline-none tw-cursor-pointer hover:md:tw-text-slate-500 md:tw-ease-out md:tw-duration-200 active:md:tw-translate-x-1"
                    >
                        <dk-icon name="ds-chevron-right" size="s" />
                    </button>
                </div>
            </slot>
        </div>
    </div>
</template>

<script>
import debounce from 'lodash/debounce';
import { swiper, swiperSlide } from 'vue-awesome-swiper';
import { EventBus } from '@v4/common/utils/event-bus';

/**
 * This component is similar to DkSliderDST, but separated into
 * an independent component because of its peculiarities.
 * Some methods (allowSlide, onReady, onResize, partially onTap)
 * and a part of HTML structure are taken from DkSliderDST.
 */

export default {
    name: 'ImageSwiper',
    components: {
        DkIcon: () => ({
            component: import(/* webpackChunkName: "DkIcon-vue" */ '@v4/common/components/atoms/DkIcon.vue'),
        }),
        swiper,
        // eslint-disable-next-line vue/no-unused-components
        swiperSlide,
    },
    props: {
        /**
         * @see [Swiper Parameters](http://idangero.us/swiper/api/#parameters).
         */
        swiperOpts: {
            type: Object,
            default: () => ({}),
        },
        isMobileVersion: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            isGalleryOpen: false,
        };
    },
    computed: {
        swiper() {
            return this.$refs.swiper.swiper;
        },
        swiperOptions() {
            return {
                navigation: {
                    nextEl: `.vdp-image-swiper__button-next.next-${this._uid}`,
                    prevEl: `.vdp-image-swiper__button-prev.prev-${this._uid}`,
                },
                spaceBetween: this.isMobileVersion ? 16 : 24,
                slidesPerView: 'auto',
                centeredSlides: true,
                loop: true,
                ...this.swiperOpts,
            };
        },
        activeIndex() {
            return this.swiper.activeIndex;
        },
    },
    mounted() {
        EventBus.$on('YoutubeMediaGallery:slide-change', this.alignCurrentSlideToGallery);
    },
    beforeDestroy() {
        EventBus.$off('YoutubeMediaGallery:slide-change', this.alignCurrentSlideToGallery);
    },
    methods: {
        /**
         * Allows the slides to slide. Depending on device and on slide numbers.
         *
         * @return {void}
         */
        allowSlide() {
            const slides = this.swiper.slides.length;

            if (!slides) {
                return;
            }

            if (
                (!this.isMobileVersion && (slides === 1 || slides <= this.swiperOptions.slidesPerView)) ||
                (this.isMobileVersion && slides === 1)
            ) {
                this.swiper.allowSlidePrev = false;
                this.swiper.allowSlideNext = false;
            }
        },
        onReady() {
            /**
             * Fired when swiper slider is ready.
             *
             * @event ready
             * @type {object} Swiper instance
             */
            this.$emit('ready', this.swiper);
            const slides = this.swiper.slides.length;

            if (slides === 1) {
                this.classes.push('has-one');
            } else if (slides <= this.swiperOptions.slidesPerView) {
                this.classes.push('has-less-then-default');
            }

            this.allowSlide();

            /*
             * Sometimes the swiper is loaded broken,
             * with no error in the console or any clear reason.
             * It is probably given by the complexity of our slider.
             * To reassure that the swiper presents correctly,
             * we need to fix the loop mode manually.
             *
             * In the VideoSwiper we don't need setTimeout, because the fixing
             * occurs not when the swiper is ready, but when the YouTube frames are ready,
             * so with a bit of delay. In this case, we need to manually simulate this delay.
             */
            setTimeout(() => {
                if (this.activeIndex === this.swiper.slides.length - 1) {
                    this.fixSwiperLoop();
                }
            }, 500);
        },
        /**
         * Using debounce in order to avoid emitting events multiple times.
         *
         * @see {@link https://github.com/surmon-china/vue-awesome-swiper/issues/286| Click event emit twice}
         * @return {void}
         */
        onTap: debounce(function() {
            let { clickedIndex } = this.swiper;
            const { clickedSlide } = this.swiper;

            if (clickedSlide) {
                const slideIndex = clickedSlide.getAttribute('data-swiper-slide-index');

                if (this.swiperOptions.loop && slideIndex) {
                    clickedIndex = parseInt(slideIndex, 10);
                }

                /**
                 * Fired when a specific slide is clicked.
                 *
                 * @event clickedSlide
                 * @type {object}
                 * @property {number} clickedIndex Slide index
                 * @property {object} clickedSlide Slide HTML element
                 * @property {object} $event PointerEvent
                 */

                // Click handler to open media gallery
                this.onSlideClick(clickedIndex);
            }
        }),
        /**
         * Using debounce in order to avoid emitting events multipe times.
         *
         * @see {@link https://github.com/surmon-china/vue-awesome-swiper/issues/286| Click event emit twice}
         * @return {void}
         */
        onResize: debounce(function() {
            this.allowSlide();
        }),
        onSlideClick(clickedIndex) {
            this.$emit('slide-click', clickedIndex);
        },
        alignCurrentSlideToGallery(index) {
            if (this.activeIndex !== index) {
                this.swiper.slideTo(index);
            }
        },
        fixSwiperLoop() {
            const firstSlideIndex = this.swiper.slides.length / 3;
            this.swiper.slideTo(firstSlideIndex);
        },
    },
};
</script>
